import { createRoute, useNavigate } from '@tanstack/react-router';
import { Route as authedRoute } from '../_authed';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listMailboxes,
  listFolders,
  listMessages,
  getMessage,
  sendReply,
  moveMessage,
  searchMessages,
  fetchAttachmentUrl,
  setSeen,
} from '@/features/mail';

function fmtDate(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      {children}
    </Box>
  );
}

function MailboxPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string>('INBOX');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(50);
  const [msgOffset, setMsgOffset] = useState<number>(0);
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [sendAs, setSendAs] = useState(false);
  const [moveAnchor, setMoveAnchor] = useState<null | HTMLElement>(null);
  const [draggedUid, setDraggedUid] = useState<string | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mailboxesQ = useQuery({ queryKey: ['mailboxes'], queryFn: listMailboxes });

  useEffect(() => {
    if (!selectedMailbox && mailboxesQ.data && mailboxesQ.data.length > 0) {
      setSelectedMailbox(mailboxesQ.data[0].address);
    }
  }, [mailboxesQ.data, selectedMailbox]);

  const currentMailbox = useMemo(
    () => mailboxesQ.data?.find((m) => m.address === selectedMailbox) ?? null,
    [mailboxesQ.data, selectedMailbox]
  );

  const foldersQ = useQuery({
    queryKey: ['folders', selectedMailbox],
    queryFn: () => listFolders(selectedMailbox as string),
    enabled: !!selectedMailbox,
  });

  const messagesQ = useQuery({
    queryKey: ['messages', selectedMailbox, selectedFolder, pageSize, msgOffset, unreadOnly],
    queryFn: () => listMessages(selectedMailbox as string, selectedFolder, pageSize, msgOffset, unreadOnly),
    enabled: !!selectedMailbox,
  });

  const seenMut = useMutation({
    mutationFn: (v: { uid: string; seen: boolean }) =>
      setSeen(selectedMailbox as string, v.uid, v.seen, selectedFolder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', selectedMailbox, selectedFolder] });
      queryClient.invalidateQueries({ queryKey: ['folders', selectedMailbox] });
    },
  });

  const bodyQ = useQuery({
    queryKey: ['message', selectedMailbox, selectedFolder, selectedUid],
    queryFn: () => getMessage(selectedMailbox as string, selectedUid as string, selectedFolder),
    enabled: !!selectedMailbox && !!selectedUid,
  });

  // ---- search across all folders ----
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sFields, setSFields] = useState({ sender: true, recipient: true, subject: true, body: false });
  const searchQ = useQuery({
    queryKey: ['search', selectedMailbox, searchQuery, sFields],
    queryFn: () => searchMessages(selectedMailbox as string, searchQuery, sFields),
    enabled: !!selectedMailbox && !!searchQuery,
  });
  const searching = !!searchQuery;

  function runSearch() {
    const q = searchInput.trim();
    if (!q) return;
    setSelectedUid(null);
    setSearchQuery(q);
  }
  function clearSearch() {
    setSearchInput('');
    setSearchQuery('');
  }

  // Customer alias this message was delivered to (resolved server-side from the
  // mailbox's Sieve alias-map; set only when the caller may send-as). Covers
  // both netyo.de and signalio.de customers.
  const aliasCandidate = bodyQ.data?.send_as_alias ?? null;
  const canSendAs = !!aliasCandidate;

  // Default reply recipient = the original sender's address (editable, so a
  // manager can reply — incl. as the customer — to a NEW address).
  const senderEmail = (() => {
    const m = (bodyQ.data?.from ?? '').match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
    return m ? m[0] : '';
  })();

  const replyMut = useMutation({
    mutationFn: (text: string) =>
      sendReply(
        selectedMailbox as string,
        selectedUid as string,
        text,
        selectedFolder,
        replyTo.trim() || undefined,
        sendAs && aliasCandidate ? aliasCandidate : undefined
      ),
    onSuccess: (r) => {
      setSnack({ msg: `${t('mail.compose.sent')} → ${r.to}`, sev: 'success' });
      setReplyOpen(false);
      setReplyText('');
      setReplyTo('');
      setSendAs(false);
    },
    onError: () => setSnack({ msg: t('mail.compose.error'), sev: 'error' }),
  });

  const moveMut = useMutation({
    mutationFn: (vars: { uid: string; dest: string }) =>
      moveMessage(selectedMailbox as string, vars.uid, vars.dest, selectedFolder),
    onSuccess: (_data, vars) => {
      setSnack({ msg: t('mail.move.moved'), sev: 'success' });
      setMoveAnchor(null);
      if (vars.uid === selectedUid) setSelectedUid(null);
      queryClient.invalidateQueries({ queryKey: ['messages', selectedMailbox, selectedFolder] });
    },
    onError: () => setSnack({ msg: t('mail.move.error'), sev: 'error' }),
  });

  function handleDropOnFolder(folderName: string) {
    setDragOverFolder(null);
    const uid = draggedUid;
    setDraggedUid(null);
    if (uid && folderName !== selectedFolder) {
      moveMut.mutate({ uid, dest: folderName });
    }
  }

  function switchMailbox(addr: string) {
    setSelectedMailbox(addr);
    setSelectedFolder('INBOX');
    setSelectedUid(null);
    setMsgOffset(0);
    setReplyOpen(false);
    clearSearch();
  }
  function switchFolder(name: string) {
    setSelectedFolder(name);
    setSelectedUid(null);
    setMsgOffset(0);
    setReplyOpen(false);
    clearSearch();
  }
  function openSearchHit(folder: string, uid: string) {
    setSelectedFolder(folder);
    setSelectedUid(uid);
    setReplyOpen(false);
  }
  async function openAttachment(index: number) {
    try {
      const url = await fetchAttachmentUrl(
        selectedMailbox as string,
        selectedUid as string,
        index,
        selectedFolder
      );
      window.open(url, '_blank');
    } catch {
      setSnack({ msg: t('mail.attach.error'), sev: 'error' });
    }
  }
  function createFilterFromEmail() {
    const fromRaw = bodyQ.data?.from ?? '';
    const m = fromRaw.match(/<([^>]+)>/);
    const email = (m ? m[1] : fromRaw).trim();
    if (!email) return;
    navigate({
      to: '/filters',
      search: { prefillFrom: email, prefillName: email, prefillMailbox: selectedMailbox ?? undefined },
    });
  }
  function fmtSize(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  // ---- mailbox switcher (top-right dropdown) ----
  const switcher = (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="mb-switch-label">{t('mail.mailbox.mailboxLabel')}</InputLabel>
      <Select
        labelId="mb-switch-label"
        label={t('mail.mailbox.mailboxLabel')}
        value={selectedMailbox ?? ''}
        onChange={(e) => switchMailbox(e.target.value)}
      >
        {mailboxesQ.data?.map((mb) => (
          <MenuItem key={mb.address} value={mb.address}>
            {mb.display_name} — {mb.address}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // ---- pane 1: folders ----
  // Annotate each folder with its hierarchy depth (nesting under the IMAP "."
  // separator) and re-sort so children follow their parent — otherwise nested
  // folders (e.g. INBOX.Mitarbeiter.Igor) render as scattered flat rows.
  const treeFolders = (foldersQ.data ?? [])
    .map((f) => {
      const upper = f.name.toUpperCase();
      const rel = upper === 'INBOX' ? '' : upper.startsWith('INBOX.') ? f.name.slice(6) : f.name;
      const depth = rel === '' ? 0 : rel.split('.').length - 1;
      const sortKey = f.special === 'inbox' ? '' : rel.toLowerCase();
      return { ...f, depth, sortKey };
    })
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  const folderPane = (
    <Box sx={{ width: 220, flexShrink: 0, height: '100%', overflow: 'auto' }}>
      {foldersQ.isLoading && <Centered><CircularProgress size={22} /></Centered>}
      {foldersQ.isError && <Box sx={{ p: 2 }}><Alert severity="error">{t('mail.mailbox.loadError')}</Alert></Box>}
      <List dense>
        {treeFolders.map((f) => (
          <ListItem key={f.name} disablePadding>
            <ListItemButton
              selected={selectedFolder === f.name}
              onClick={() => switchFolder(f.name)}
              onDragOver={(e) => {
                if (draggedUid && f.name !== selectedFolder) {
                  e.preventDefault();
                  setDragOverFolder(f.name);
                }
              }}
              onDragLeave={() => setDragOverFolder((cur) => (cur === f.name ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                handleDropOnFolder(f.name);
              }}
              sx={{
                pl: 1 + f.depth * 1.75,
                ...(dragOverFolder === f.name
                  ? { outline: `2px dashed ${theme.palette.primary.main}`, outlineOffset: -2 }
                  : {}),
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <span style={{ fontWeight: f.unread > 0 ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                    <Typography variant="caption" sx={{ flexShrink: 0 }} color={f.unread > 0 ? 'primary' : 'textSecondary'}>
                      {f.unread > 0 ? `${f.unread}/${f.total}` : f.total}
                    </Typography>
                  </Stack>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // ---- pane 2: message list ----
  const listPane = (
    <Box
      sx={{
        width: { xs: '100%', md: 360 },
        flexShrink: 0,
        height: '100%',
        overflow: 'auto',
        borderLeft: { md: `1px solid ${theme.palette.divider}` },
        borderRight: { md: `1px solid ${theme.palette.divider}` },
      }}
    >
      {searching ? (
        <>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {searchQ.data ? `${searchQ.data.hits.length} ${t('mail.search.results')}` : t('mail.search.searching')}
            </Typography>
            {searchQ.data && searchQ.data.also_searched.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" color="textSecondary">{t('mail.search.alsoSearched')}</Typography>
                {searchQ.data.also_searched.map((a) => (
                  <Chip key={a} size="small" color="info" variant="outlined" label={a}
                    sx={{ height: 18, fontSize: '0.65rem' }} />
                ))}
              </Box>
            )}
          </Box>
          <Divider />
          {searchQ.isLoading && <Centered><CircularProgress size={22} /></Centered>}
          {searchQ.isError && <Box sx={{ p: 2 }}><Alert severity="error">{t('mail.mailbox.loadError')}</Alert></Box>}
          {searchQ.data && searchQ.data.hits.length === 0 && (
            <Box sx={{ p: 2 }}><Typography variant="body2" color="textSecondary">{t('mail.search.none')}</Typography></Box>
          )}
          <List dense disablePadding>
            {searchQ.data?.hits.map((h) => (
              <ListItem key={h.folder + ':' + h.uid} disablePadding divider>
                <ListItemButton
                  selected={selectedUid === h.uid && selectedFolder === h.folder}
                  onClick={() => openSearchHit(h.folder, h.uid)}
                  sx={{ display: 'block', py: 1 }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: h.seen ? 400 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.from_name || h.from_addr}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ flexShrink: 0 }}>
                      {fmtDate(h.date, i18n.language)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {h.has_attachments && <AttachFileIcon sx={{ fontSize: 14 }} color="action" />}
                    <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.subject || t('mail.mailbox.noSubject')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip size="small" variant="outlined" label={h.folder_label} sx={{ height: 18, fontSize: '0.65rem' }} />
                    {h.via_alias && (
                      <Chip size="small" color="info" label={t('mail.search.viaAlias')} title={h.via_alias}
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      ) : (
        <>
      {messagesQ.isLoading && <Centered><CircularProgress size={22} /></Centered>}
      {messagesQ.isError && <Box sx={{ p: 2 }}><Alert severity="error">{t('mail.mailbox.loadError')}</Alert></Box>}
      {messagesQ.data && (
        <>
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {messagesQ.data.total === 0
                ? `0 ${t('mail.mailbox.messagesCount')}`
                : `${msgOffset + 1}–${Math.min(msgOffset + pageSize, messagesQ.data.total)} / ${messagesQ.data.total}`}
            </Typography>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={unreadOnly}
                  onChange={(e) => { setUnreadOnly(e.target.checked); setMsgOffset(0); }}
                />
              }
              label={<Typography variant="caption">{t('mail.mailbox.unreadOnly')}</Typography>}
            />
            <FormControl size="small" variant="standard" sx={{ minWidth: 64 }}>
              <Select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setMsgOffset(0); }}
              >
                {[50, 100, 200, 500].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Divider />
          <List dense disablePadding>
            {messagesQ.data.items.map((e) => (
              <ListItem key={e.uid} disablePadding divider>
                <ListItemButton
                  selected={selectedUid === e.uid}
                  onClick={() => {
                    setSelectedUid(e.uid);
                    setReplyOpen(false);
                  }}
                  draggable
                  onDragStart={(ev) => {
                    setDraggedUid(e.uid);
                    ev.dataTransfer.effectAllowed = 'move';
                    ev.dataTransfer.setData('text/plain', e.uid);
                  }}
                  onDragEnd={() => {
                    setDraggedUid(null);
                    setDragOverFolder(null);
                  }}
                  sx={{ display: 'block', py: 1 }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: e.seen ? 400 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {e.from_name || e.from_addr}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ flexShrink: 0 }}>
                      {fmtDate(e.date, i18n.language)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {e.has_attachments && <AttachFileIcon sx={{ fontSize: 14 }} color="action" />}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontWeight: e.seen ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {e.subject || t('mail.mailbox.noSubject')}
                    </Typography>
                  </Stack>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {messagesQ.data.total > pageSize && (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ p: 1 }}>
              <Button size="small" disabled={msgOffset === 0}
                onClick={() => { setMsgOffset(Math.max(0, msgOffset - pageSize)); setSelectedUid(null); }}>
                ← {t('mail.logs.prev')}
              </Button>
              <Typography variant="caption" color="textSecondary">
                {Math.floor(msgOffset / pageSize) + 1} / {Math.ceil(messagesQ.data.total / pageSize)}
              </Typography>
              <Button size="small" disabled={msgOffset + pageSize >= messagesQ.data.total}
                onClick={() => { setMsgOffset(msgOffset + pageSize); setSelectedUid(null); }}>
                {t('mail.logs.next')} →
              </Button>
            </Stack>
          )}
        </>
      )}
        </>
      )}
    </Box>
  );

  // ---- pane 3: reading pane ----
  const readingPane = (
    <Box sx={{ flex: 1, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      {!selectedUid && <Centered><Typography color="textSecondary">{t('mail.mailbox.noMessageSelected')}</Typography></Centered>}
      {selectedUid && bodyQ.isLoading && <Centered><CircularProgress /></Centered>}
      {selectedUid && bodyQ.isError && <Box sx={{ p: 2 }}><Alert severity="error">{t('mail.mailbox.loadError')}</Alert></Box>}
      {selectedUid && bodyQ.data && (
        <>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography variant="h3" component="h1">
                {bodyQ.data.subject || t('mail.mailbox.noSubject')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DriveFileMoveIcon />}
                  onClick={(e) => setMoveAnchor(e.currentTarget)}
                  disabled={moveMut.isPending}
                >
                  {t('mail.move.move')}
                </Button>
                <Menu anchorEl={moveAnchor} open={!!moveAnchor} onClose={() => setMoveAnchor(null)}>
                  {foldersQ.data
                    ?.filter((f) => f.name !== selectedFolder)
                    .map((f) => (
                      <MenuItem
                        key={f.name}
                        onClick={() => selectedUid && moveMut.mutate({ uid: selectedUid, dest: f.name })}
                      >
                        {f.label}
                      </MenuItem>
                    ))}
                </Menu>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterAltIcon />}
                  onClick={createFilterFromEmail}
                >
                  {t('mail.nav.filters')}
                </Button>
                <Button variant="outlined" size="small" disabled={seenMut.isPending}
                  onClick={() => selectedUid && seenMut.mutate({ uid: selectedUid, seen: false })}>
                  {t('mail.mailbox.markUnread')}
                </Button>
                <Button variant="outlined" size="small" disabled={seenMut.isPending}
                  onClick={() => selectedUid && seenMut.mutate({ uid: selectedUid, seen: true })}>
                  {t('mail.mailbox.markRead')}
                </Button>
                {currentMailbox?.can_reply && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReplyIcon />}
                    onClick={() => setReplyOpen((v) => { const open = !v; if (open) setReplyTo(senderEmail); return open; })}
                  >
                    {t('mail.compose.reply')}
                  </Button>
                )}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
              <Chip size="small" label={bodyQ.data.from} />
              <Typography variant="caption" color="textSecondary">
                {fmtDate(bodyQ.data.date, i18n.language)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
              {t('mail.mailbox.to')}: {bodyQ.data.to}
            </Typography>
            {bodyQ.data.attachments.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
                <AttachFileIcon fontSize="small" color="action" />
                {bodyQ.data.attachments.map((a) => (
                  <Chip
                    key={a.index}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => openAttachment(a.index)}
                    label={`${a.filename} · ${fmtSize(a.size)}`}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {replyOpen && (
            <Box sx={{ px: 2, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={t('mail.mailbox.to')}
                placeholder={senderEmail}
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                disabled={replyMut.isPending}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                autoFocus
                placeholder={t('mail.compose.placeholder')}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={replyMut.isPending}
              />
              {canSendAs && (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={sendAs}
                      onChange={(e) => setSendAs(e.target.checked)}
                      disabled={replyMut.isPending}
                    />
                  }
                  label={t('mail.compose.sendAs', { alias: aliasCandidate })}
                />
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={replyMut.isPending || !replyText.trim()}
                  onClick={() => replyMut.mutate(replyText)}
                >
                  {replyMut.isPending ? t('mail.compose.sending') : t('mail.compose.send')}
                </Button>
                <Button size="small" onClick={() => setReplyOpen(false)} disabled={replyMut.isPending}>
                  {t('mail.compose.cancel')}
                </Button>
              </Stack>
            </Box>
          )}

          <Divider />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {bodyQ.data.html ? (
              <iframe
                title="message-body"
                // allow-popups (+ escape) lets links/confirm-buttons open in a new
                // tab; NO allow-scripts → email JS still cannot run (untrusted).
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                srcDoc={`<base target="_blank">${bodyQ.data.html}`}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
            ) : (
              <Box component="pre" sx={{ p: 2, m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                {bodyQ.data.text || t('mail.mailbox.emptyBody')}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const topBar = (
    <Toolbar variant="dense" sx={{ borderBottom: `1px solid ${theme.palette.divider}`, gap: 1, flexWrap: 'wrap', py: 0.5 }}>
      {isMobile && selectedUid && (
        <IconButton edge="start" size="small" onClick={() => setSelectedUid(null)} aria-label="back">
          <ArrowBackIcon />
        </IconButton>
      )}
      <TextField
        size="small"
        placeholder={t('mail.search.placeholder')}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') runSearch();
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searching ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={clearSearch} aria-label="clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{ minWidth: 200 }}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={sFields.sender} onChange={(e) => setSFields((s) => ({ ...s, sender: e.target.checked }))} />}
        label={t('mail.search.from')}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={sFields.recipient} onChange={(e) => setSFields((s) => ({ ...s, recipient: e.target.checked }))} />}
        label={t('mail.search.to')}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={sFields.subject} onChange={(e) => setSFields((s) => ({ ...s, subject: e.target.checked }))} />}
        label={t('mail.search.subject')}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={sFields.body} onChange={(e) => setSFields((s) => ({ ...s, body: e.target.checked }))} />}
        label={t('mail.search.body')}
      />
      <Button size="small" variant="outlined" onClick={runSearch} disabled={!searchInput.trim()}>
        {t('mail.search.search')}
      </Button>
      <Box sx={{ flex: 1 }} />
      {switcher}
    </Toolbar>
  );

  let body: React.ReactNode;
  if (isMobile) {
    body = selectedUid ? readingPane : (
      <Box sx={{ display: 'flex', height: '100%' }}>
        {folderPane}
        {listPane}
      </Box>
    );
  } else {
    body = (
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        {folderPane}
        {listPane}
        {readingPane}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {topBar}
      <Box sx={{ flex: 1, minHeight: 0 }}>{body}</Box>
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export const Route = createRoute({
  getParentRoute: () => authedRoute,
  path: 'mailbox',
  component: MailboxPage,
});
